import React, { useState, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'

import {
  loadConfig,
  fetchDataForItems_aggregated,
  fetchPrices_aggregated,
  addToCart_aggregated,
} from './store/requests/thunks'
import { handleCustomer } from './store/customer/customerSlice'
import { REJECTED, CART } from './constants/constants'
import ProceedButton from './components/AppHeader/ProceedButton/ProceedButton'
import ArticlesList from './components/ArticlesList/ArticlesList'
import DeliveryInfo from './components/DeliveryInfo/DeliveryInfo'
import PriceSummary from './components/PriceSummary/PriceSummary'
import * as Styled from './styles'
import { Button } from './components/styles'
import { useAppDispatch, useAppSelector } from './hooks'
import * as DynamakerContract from './integration/DynamakerContract'

interface Props {
  isDisabled: boolean
  articleErpitemnumber?: string
  articlePayoff?: string
}

const ProductPageApp: React.FC<Props> = ({ articleErpitemnumber, articlePayoff, isDisabled }) => {
  const dispatch = useAppDispatch()

  const urlParams = new URLSearchParams(window.location.search)
  const sibhcFromUrl = urlParams.get('sibhc')

  const { cnohash } = useAppSelector((state) => state.customer)
  const { content } = useAppSelector((state) => state.init)
  const { requestStatus_loadConfig, requestStatus_fetchPrices_aggregated } = useAppSelector(
    (state) => state.requests.requestStatus
  )
  const { configId, configHashKey } = useAppSelector((state) => state.yass)

  const [hasError, setHasError] = useState(false)

  const [dmDataFromYass, setDmDataFromYass] = useState<{
    config: any
    configurationSections: DynamakerContract.SectionedBOM
    meta: DynamakerContract.Metadata
  }>({
    config: {},
    configurationSections: [],
    meta: {} as DynamakerContract.Metadata,
  })

  useEffect(() => {
    if (isDisabled) {
      console.log(`Article ${articleErpitemnumber} is disabled`)
      setHasError(true)
      return
    }

    if (!articlePayoff) {
      console.error(`No article payoff from PIM for ${articleErpitemnumber}`)
      setHasError(true)
      return
    }

    if (!articlePayoff.includes(':')) {
      console.error(`Unexpected value for article payoff for ${articleErpitemnumber} from PIM (no ':' char)`)
      setHasError(true)
      return
    }

    const configIdFromPIM = articlePayoff.slice(0, articlePayoff.indexOf(':')).trim()
    const configHashKeyFromPIM = articlePayoff.slice(articlePayoff.indexOf(':') + 1).trim()

    if (!configIdFromPIM || !configHashKeyFromPIM) {
      console.error(
        `Unexpected value for article payoff for ${articleErpitemnumber} from PIM (no configId and/or configHashKey)`
      )
      setHasError(true)
      return
    }

    if (sibhcFromUrl) {
      dispatch(handleCustomer({ cnohash: sibhcFromUrl }))
    }

    dispatch(
      loadConfig({
        configId: configIdFromPIM,
        configHashKey: configHashKeyFromPIM,
      })
    )
      .unwrap()
      .then(async (result) => {
        const {
          type,
          dmConfigData: configFromYass,
          dmConfigurationSections: configurationSectionsFromYass, // TODO 5625: Diskutera namnsättning bom vs configurationSections framöver
          dmMetadata: metaFromYass,
        } = result

        if (type !== 'PRESET') {
          console.error(
            `Article payoff for ${articleErpitemnumber} from PIM does not match a saved config of type 'PRESET' (configId ${configIdFromPIM} has type ${type})`
          )
          setHasError(true)
          return
        }

        setDmDataFromYass({
          config: configFromYass,
          configurationSections: configurationSectionsFromYass,
          meta: metaFromYass,
        })

        /*
          For items with configData (mallisar), fetch article data from Scensum inclucing PricingInfo
          (PrincingInfo is needed to fetch price from LFC backend for these articles)
        */
        const itemsToFetch = configurationSectionsFromYass
          .map((section) =>
              section.articles.flatMap((item) =>
                ('configData' in item && item.configData !== undefined)
                  ? [
                      {
                        props: { configData: item.configData },
                        erpItemNumber: item.erpItemNumber,
                      },
                    ]
                  : []
              )
          )
          .flat()

        await dispatch(fetchDataForItems_aggregated({ items: itemsToFetch, articlesInConfig: new Set() })).then(
          async () => {
            await dispatch(
              fetchPrices_aggregated({
                bom: configurationSectionsFromYass,
                meta: metaFromYass,
              })
            )
          }
        )
      })
      .catch((err) => {
        console.error(err)
      })

    // (No cleanup)
  }, [articleErpitemnumber, articlePayoff, dispatch, isDisabled, sibhcFromUrl])

  if (hasError || requestStatus_loadConfig === REJECTED || requestStatus_fetchPrices_aggregated === REJECTED) {
    return (
      <div style={{ margin: '30px 15px' }}>
        <p>
          <strong>Vi ber om ursäkt, ett tekniskt fel har inträffat.</strong>
        </p>
        <p>Vänligen försök igen. Om problemet kvarstår, kontakta vår kundservice.</p>
      </div>
    )
  }

  return (
    <Styled.ProductPageApp>
      <ArticlesList />
      <PriceSummary />
      <ProceedButton
        onRequestSaveProject={({ type }) => {
          if (type !== CART) {
            console.error('Unexpected error in ProductPageApp: type !== CART')
            setHasError(true)
            return { internalSaveUuidv4: null }
          }

          const id = uuidv4()

          const { config, configurationSections, meta } = dmDataFromYass

          dispatch(
            addToCart_aggregated({
              config,
              bom: configurationSections,
              meta,
              internalSaveUuidv4: id,
            })
          ).catch((err) => {
            console.error(err)
          })

          return { internalSaveUuidv4: id }
        }}
        buttonText={content.buttons.addToCart_variant}
      />
      {configId && configHashKey ? (
        <a
          className="button-secondary-large-arrow button-continue-to-configurator"
          // (link will not work on localhost)
          href={`${content.other.toConfiguratorLinkPath}?configId=${configId}&configHashKey=${configHashKey}${
            cnohash ? `&sibhc=${cnohash}` : ''
          }`}
        >
          {content.buttons.toConfigurator}
        </a>
      ) : (
        <Button
          className="button-secondary-large-arrow button-continue-to-configurator"
          disabled={true}
          onClick={() => {}}
        >
          {content.buttons.toConfigurator}
        </Button>
      )}
      {(!import.meta.env.VITE_HIDE_DELIVERY_INFO || import.meta.env.VITE_HIDE_DELIVERY_INFO === 'false') && (
        <DeliveryInfo />
      )}
    </Styled.ProductPageApp>
  )
}

export default ProductPageApp
